import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';

export type AssetFilterOption = string | { value: string; label: string };

export type AssetFilterColumn = {
  key: string;
  label: string;
  filterOptions?: AssetFilterOption[];
};

type AssetsFilterBarProps = {
  filters: {
    [key: string]: string | string[];
  };
  onFilterChange: (filterType: string, values: string[]) => void;
  columns: AssetFilterColumn[];
  onClearAll: () => void;
  hasActiveFilters: boolean;
};

/**
 * Popover contents for the Assets tab. Stacked rather than inline because the
 * whole set now lives behind one trigger, which is also why the narrow
 * (map-open) layout no longer has to drop filters to fit.
 */
export function AssetsFilterBar({
  filters,
  onFilterChange,
  columns,
  onClearAll,
  hasActiveFilters,
}: AssetsFilterBarProps) {
  return (
    <div className="tw:space-y-2">
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <span className="tw:text-xs tw:font-medium tw:text-foreground">
          Filters
        </span>
        {hasActiveFilters ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="tw:h-auto tw:px-1 tw:py-0.5 tw:text-xs"
            onClick={onClearAll}
          >
            Clear all
          </Button>
        ) : null}
      </div>
      {columns.map(({ key, label, filterOptions }) => {
        const currentValues = filters[key];
        const valueArray = Array.isArray(currentValues)
          ? currentValues
          : currentValues
            ? [currentValues]
            : [];

        const normalizedOptions = (filterOptions ?? []).map((option) =>
          typeof option === 'string' ? { value: option, label: option } : option
        );

        return (
          <Field key={key} className="tw:min-w-0 tw:gap-1">
            <FieldLabel className="tw:text-xs tw:text-muted-foreground">
              {label}
            </FieldLabel>
            {key === 'name' ? (
              <Input
                value={valueArray[0] || ''}
                onChange={(event) =>
                  onFilterChange(
                    key,
                    event.target.value ? [event.target.value] : []
                  )
                }
                placeholder={`Search ${label}…`}
                aria-label={`Search ${label}`}
                className="tw:h-8"
              />
            ) : normalizedOptions.length > 0 ? (
              <MultiIdCombobox
                options={normalizedOptions}
                value={valueArray}
                onChange={(next) => onFilterChange(key, next)}
                placeholder={`Filter ${label}…`}
              />
            ) : null}
          </Field>
        );
      })}
    </div>
  );
}

export default AssetsFilterBar;
